import React, { useState, useRef, useEffect } from 'react';
import apiFetch, { API_BASE } from '../../services/api';
import {
    Clock, PenTool, ChevronDown, ChevronUp, Bold, Italic, Underline, X, SendHorizontal,
    Paperclip, FileText, Image as ImageIcon, Folder, Smile, Table,
    ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Trash2, AlignLeft,
    AlignCenter, AlignRight, AlignJustify, PaintBucket, Split, MoreHorizontal, Layout,
    Grid3X3, List, ListOrdered, Outdent, Indent, Type, Layers, Rows, Columns, Search,
    Highlighter, Palette, Eraser, Check
} from 'lucide-react';
import { motion } from 'framer-motion';

const ImageResizer = ({ img, containerRef, onClose }) => {
    const [rect, setRect] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const isResizing = useRef(false);
    const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const aspectRatio = useRef(1);

    const updateRect = () => {
        if (!img || !containerRef.current) return;
        const imgRect = img.getBoundingClientRect();
        const containerRect = containerRef.current.parentElement.getBoundingClientRect();
        
        if (!document.contains(img)) {
            onClose();
            return;
        }

        setRect({
            top: imgRect.top - containerRect.top,
            left: imgRect.left - containerRect.left,
            width: imgRect.width,
            height: imgRect.height
        });
    };

    useEffect(() => {
        updateRect();
        const observer = new ResizeObserver(updateRect);
        observer.observe(img);
        return () => observer.disconnect();
    }, [img]);

    useEffect(() => {
        window.addEventListener('resize', updateRect);
        return () => window.removeEventListener('resize', updateRect);
    }, []);

    const handleMouseDown = (e, corner) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = corner;
        startPos.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
        aspectRatio.current = rect.width / rect.height;
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!isResizing.current) return;
        const deltaX = e.clientX - startPos.current.x;
        const deltaY = e.clientY - startPos.current.y;
        
        let newWidth = startPos.current.width;
        let newHeight = startPos.current.height;
        
        const corner = isResizing.current;
        if (corner.includes('e')) newWidth += deltaX;
        if (corner.includes('w')) newWidth -= deltaX;
        if (corner.includes('s')) newHeight += deltaY;
        if (corner.includes('n')) newHeight -= deltaY;

        if (corner.length === 2 && aspectRatio.current > 0) {
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                newHeight = newWidth / aspectRatio.current;
            } else {
                newWidth = newHeight * aspectRatio.current;
            }
        }

        if (newWidth > 20 && newHeight > 20) {
            img.style.width = `${Math.round(newWidth)}px`;
            img.style.height = `${Math.round(newHeight)}px`;
            updateRect();
        }
    };

    const handleMouseUp = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    if (!img) return null;

    const handleStyle = {
        position: 'absolute',
        width: '12px',
        height: '12px',
        backgroundColor: 'white',
        border: '2px solid var(--primary)',
        borderRadius: '50%',
        zIndex: 100
    };

    return (
        <div 
            className="image-resizer"
            style={{
                position: 'absolute',
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                border: '2px dashed var(--primary)',
                pointerEvents: 'none',
                zIndex: 50
            }}
        >
            <div style={{ position: 'absolute', top: -30, right: 0, pointerEvents: 'auto', display: 'flex', gap: '4px' }}>
                 <button onClick={() => { img.style.width = '100%'; img.style.height = 'auto'; updateRect(); }} title="Fit Width" style={{ background: 'var(--bg-main)', border: '1px solid var(--border-glass)', borderRadius: '4px', padding: '4px 8px', fontSize: '0.7rem', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', color: 'var(--text-main)' }}>Fit</button>
                 <button onClick={() => { img.style.width = 'auto'; img.style.height = 'auto'; updateRect(); }} title="Original Size" style={{ background: 'var(--bg-main)', border: '1px solid var(--border-glass)', borderRadius: '4px', padding: '4px 8px', fontSize: '0.7rem', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', color: 'var(--text-main)' }}>1:1</button>
                 <button onClick={onClose} style={{ background: 'var(--bg-main)', border: '1px solid var(--border-glass)', borderRadius: '5px', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', color: 'var(--text-main)' }}>
                     <X size={12} />
                 </button>
            </div>
            <div style={{ ...handleStyle, top: -6, left: -6, cursor: 'nwse-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'nw')} />
            <div style={{ ...handleStyle, top: -6, right: -6, cursor: 'nesw-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'ne')} />
            <div style={{ ...handleStyle, bottom: -6, left: -6, cursor: 'nesw-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'sw')} />
            <div style={{ ...handleStyle, bottom: -6, right: -6, cursor: 'nwse-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'se')} />
            <div style={{ ...handleStyle, top: -6, left: 'calc(50% - 6px)', cursor: 'ns-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'n')} />
            <div style={{ ...handleStyle, bottom: -6, left: 'calc(50% - 6px)', cursor: 'ns-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 's')} />
            <div style={{ ...handleStyle, left: -6, top: 'calc(50% - 6px)', cursor: 'ew-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'w')} />
            <div style={{ ...handleStyle, right: -6, top: 'calc(50% - 6px)', cursor: 'ew-resize', pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'e')} />
        </div>
    );
};

const RecipientInput = ({
    label,
    recipients,
    searchInput,
    setSearchInput,
    searchResults,
    isDropdownOpen,
    setIsDropdownOpen,
    type,
    addRecipient,
    removeRecipient,
    searchContacts,
    showCc,
    setShowCc,
    showBcc,
    setShowBcc
}) => {
    const isMobile = window.innerWidth <= 768;
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (highlightedIndex >= 0 && dropdownRef.current) {
            const dropdown = dropdownRef.current;
            const highlightedItem = dropdown.children[highlightedIndex];
            if (highlightedItem) {
                const dropdownRect = dropdown.getBoundingClientRect();
                const itemRect = highlightedItem.getBoundingClientRect();

                if (itemRect.bottom > dropdownRect.bottom) {
                    dropdown.scrollTop += (itemRect.bottom - dropdownRect.bottom) + 4;
                } else if (itemRect.top < dropdownRect.top) {
                    dropdown.scrollTop -= (dropdownRect.top - itemRect.top) + 4;
                }
            }
        }
    }, [highlightedIndex]);


    useEffect(() => {
        if (isDropdownOpen && searchResults.length > 0) {
            setHighlightedIndex(0);
        } else {
            setHighlightedIndex(-1);
        }
    }, [searchResults, isDropdownOpen]);


    return (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: isMobile ? '8px 0' : '10px 0',
            borderBottom: '1px solid var(--border-glass)',
            gap: isMobile ? '8px' : '12px',
            position: 'relative'
        }}>
            <span style={{
                minWidth: isMobile ? '40px' : '60px',
                color: 'var(--text-dim)',
                fontWeight: '600',
                fontSize: '0.85rem',
                marginTop: '8px'
            }}>{label}</span>
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', minHeight: '32px' }}>
                {recipients.map(r => (
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        key={r.email}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-glass)',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            color: 'var(--text-main)'
                        }}
                    >
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.65rem' }}>
                            {r.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ maxWidth: isMobile ? '120px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</span>
                        <button onClick={() => removeRecipient(r.email, type)} style={{ color: 'var(--text-dim)', border: 'none', background: 'none', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                            <X size={14} />
                        </button>
                    </motion.div>
                ))}
                <input
                    value={searchInput}
                    onChange={(e) => {
                        const val = e.target.value;
                        // Handle multiple emails (pasted or typed with delimiters)
                        if (val.includes(',') || val.includes(';') || (val.includes(' ') && val.trim().includes('@'))) {
                            const parts = val.split(/[,; ]+/).filter(Boolean);
                            const lastChar = val.charAt(val.length - 1);
                            const isDelimiter = [',', ';', ' '].includes(lastChar);

                            if (isDelimiter) {
                                parts.forEach(p => {
                                    if (p.includes('@')) {
                                        addRecipient({ email: p, name: p.split('@')[0] }, type);
                                    }
                                });
                                setSearchInput('');
                                searchContacts('', type);
                            } else {
                                const toAdd = parts.slice(0, -1);
                                const remainder = parts[parts.length - 1];
                                toAdd.forEach(p => {
                                    if (p.includes('@')) {
                                        addRecipient({ email: p, name: p.split('@')[0] }, type);
                                    }
                                });
                                setSearchInput(remainder);
                                searchContacts(remainder, type);
                            }
                        } else {
                            setSearchInput(val);
                            searchContacts(val, type);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (isDropdownOpen && searchResults.length > 0) {
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setHighlightedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
                                return;
                            }
                            if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
                                return;
                            }
                            if (e.key === 'Enter' && highlightedIndex >= 0) {
                                e.preventDefault();
                                addRecipient(searchResults[highlightedIndex], type);
                                return;
                            }
                        }

                        if ((e.key === 'Enter' || e.key === 'Tab') && searchInput) {
                            if (searchInput.includes('@')) {
                                e.preventDefault();
                                const email = searchInput.trim().replace(/[,;]/g, '');
                                addRecipient({ email, name: email.split('@')[0] }, type);
                            }
                        } else if (e.key === 'Backspace' && !searchInput && recipients.length > 0) {
                            removeRecipient(recipients[recipients.length - 1].email, type);
                        }
                    }}
                    placeholder={recipients.length === 0 ? label : ""}
                    style={{
                        flex: 1,
                        minWidth: '100px',
                        border: 'none',
                        outline: 'none',
                        fontSize: '0.95rem',
                        background: 'transparent',
                        color: 'var(--text-main)',
                        padding: '4px 0'
                    }}
                    onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                    onFocus={() => searchInput.length >= 2 && setIsDropdownOpen(true)}
                />
            </div>

            {label === 'To' && (
                <div style={{ display: 'flex', gap: '8px', padding: '8px 0' }}>
                    <button
                        onClick={() => setShowCc(!showCc)}
                        style={{ background: 'none', border: 'none', color: showCc ? 'var(--primary)' : 'var(--text-dim)', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600' }}
                    >
                        Cc
                    </button>
                    <button
                        onClick={() => setShowBcc(!showBcc)}
                        style={{ background: 'none', border: 'none', color: showBcc ? 'var(--primary)' : 'var(--text-dim)', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600' }}
                    >
                        Bcc
                    </button>
                </div>
            )}

            {isDropdownOpen && searchResults.length > 0 && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% - 4px)',
                        left: isMobile ? '0' : '72px',
                        width: isMobile ? '100%' : '380px',
                        zIndex: 2000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        padding: '4px',
                        background: '#FFFFFF',
                        border: '1px solid var(--border-glass)',
                        borderRadius: '8px',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                    {searchResults.map((result, idx) => (
                        <div
                            key={idx}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addRecipient(result, type);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 10px',
                                gap: '10px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                transition: 'background 0.2s',
                                minHeight: '44px',
                                background: idx === highlightedIndex ? 'var(--bg-secondary)' : 'transparent',
                                border: idx === highlightedIndex ? '1px solid var(--primary)' : '1px solid transparent'
                            }}
                            className="btn-ghost"
                            onMouseEnter={() => setHighlightedIndex(idx)}
                        >
                            <div style={{
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                background: `hsl(${(result.email.length * 137) % 360}, 65%, 45%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                flexShrink: 0
                            }}>
                                {result.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, gap: '1px' }}>
                                <span style={{ fontWeight: '600', fontSize: '0.8rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{result.name}</span>
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{result.email}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ComposeModal = ({ onSend, onSaveDraft, onClose, user, initialData, availableIdentities = [], activeFolder = 'INBOX' }) => {
    const getDefaultFrom = () => {
        if (initialData?.from) return initialData.from;
        if (activeFolder.startsWith('Shared/')) {
            const sharedEmail = activeFolder.substring(7);
            if (availableIdentities.includes(sharedEmail)) return sharedEmail;
        }
        return user?.email || '';
    };

    const [composeData, setComposeData] = useState({
        from: getDefaultFrom(),
        to: initialData?.to || '',
        cc: '',
        bcc: '',
        subject: initialData?.subject || '',
        draftId: initialData?.draftId || null // Preserve draftId if editing a draft
    });
    const [toRecipients, setToRecipients] = useState([]);
    const [ccRecipients, setCcRecipients] = useState([]);
    const [bccRecipients, setBccRecipients] = useState([]);

    const [toInput, setToInput] = useState('');
    const [ccInput, setCcInput] = useState('');
    const [bccInput, setBccInput] = useState('');

    const [toSearchResults, setToSearchResults] = useState([]);
    const [ccSearchResults, setCcSearchResults] = useState([]);
    const [bccSearchResults, setBccSearchResults] = useState([]);

    const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
    const [isCcDropdownOpen, setIsCcDropdownOpen] = useState(false);
    const [isBccDropdownOpen, setIsBccDropdownOpen] = useState(false);

    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [isSearching, setIsSearching] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [fontFamily, setFontFamily] = useState('Sans Serif');
    const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
    const [isAttachmentDropdownOpen, setIsAttachmentDropdownOpen] = useState(false);
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [isTablePickerOpen, setIsTablePickerOpen] = useState(false);
    const [tableHover, setTableHover] = useState({ rows: 0, cols: 0 });
    const [tableStep, setTableStep] = useState('grid'); // 'grid' or 'customize'
    const [tableStylesTab, setTableStylesTab] = useState('Grid');
    const [selectedTableStyle, setSelectedTableStyle] = useState(0);
    const [tableOptions, setTableOptions] = useState({
        headerRow: true,
        firstColumn: false,
        bandedRows: true,
        bandedColumns: false
    });
    const [tableWidth, setTableWidth] = useState(100);
    const [tableAlign, setTableAlign] = useState('Left');

    const [selectedTable, setSelectedTable] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [tableToolbarPos, setTableToolbarPos] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [tableStyles, setTableStyles] = useState({
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 0,
        backgroundColor: 'transparent',
        padding: 12
    });

    // Signature states
    const [isSignatureDropdownOpen, setIsSignatureDropdownOpen] = useState(false);
    const [isManageSignaturesOpen, setIsManageSignaturesOpen] = useState(false);
    const [isBulletDropdownOpen, setIsBulletDropdownOpen] = useState(false);
    const [isNumberingDropdownOpen, setIsNumberingDropdownOpen] = useState(false);
    const [fontSize, setFontSize] = useState(14);
    const [isFontSizeDropdownOpen, setIsFontSizeDropdownOpen] = useState(false);
    const [isTextColorOpen, setIsTextColorOpen] = useState(false);
    const [isHighlightColorOpen, setIsHighlightColorOpen] = useState(false);
    const [isAlignDropdownOpen, setIsAlignDropdownOpen] = useState(false);
    const [textColor, setTextColor] = useState('#000000');
    const [highlightColor, setHighlightColor] = useState('#FFFF00');
    const [signatures, setSignatures] = useState([]);
    const [selectedSignatureId, setSelectedSignatureId] = useState(null);
    const [isEditingSignature, setIsEditingSignature] = useState(false);
    const [editName, setEditName] = useState('');
    const [editContent, setEditContent] = useState('');
    const [newMsgSigId, setNewMsgSigId] = useState(localStorage.getItem('defaultNewMessageSigId') || 'none');
    const [replySigId, setReplySigId] = useState(localStorage.getItem('defaultReplySigId') || 'none');
    const [isIdentityDropdownOpen, setIsIdentityDropdownOpen] = useState(false);
    const [signedAccount, setSignedAccount] = useState(user?.name ? `${user.name} (${user.email})` : (user?.email || ''));

    const [activeFormats, setActiveFormats] = useState({
        bold: false,
        italic: false,
        underline: false,
        bullet: false,
        numbering: false,
        alignLeft: true,
        alignCenter: false,
        alignRight: false,
        alignJustify: false
    });

    const updateActiveFormats = () => {
        try {
            setActiveFormats({
                bold: document.queryCommandState('bold'),
                italic: document.queryCommandState('italic'),
                underline: document.queryCommandState('underline'),
                bullet: document.queryCommandState('insertUnorderedList'),
                alignRight: document.queryCommandState('justifyRight'),
                alignJustify: document.queryCommandState('justifyFull')
            });

            // Detect current font size at cursor
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                let node = selection.anchorNode;
                if (node && node.nodeType === 3) node = node.parentNode;
                if (node) {
                    const computedStyle = window.getComputedStyle(node);
                    const currentFontSize = parseInt(computedStyle.fontSize);
                    if (currentFontSize && !isNaN(currentFontSize)) {
                        setFontSize(currentFontSize);
                    }
                }
            }
        } catch (e) {
            // Some browsers might throw error if command is not supported or editor not focused
        }
    };

    const sigEditorRef = useRef(null);

    const closeAllPickers = () => {
        setIsFontDropdownOpen(false);
        setIsFontSizeDropdownOpen(false);
        setIsAttachmentDropdownOpen(false);
        setIsEmojiPickerOpen(false);
        setIsTablePickerOpen(false);
        setIsSignatureDropdownOpen(false);
        setIsBulletDropdownOpen(false);
        setIsNumberingDropdownOpen(false);
        setIsTextColorOpen(false);
        setIsHighlightColorOpen(false);
        setIsAlignDropdownOpen(false);
    };

    const togglePicker = (setter, currentState) => {
        if (!currentState) closeAllPickers();
        setter(!currentState);
    };

    // Close all pickers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.picker-container')) return;
            closeAllPickers();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSignatures = async () => {
        try {
            const resp = await apiFetch('/signatures');
            if (resp.ok) {
                const data = await resp.json();
                setSignatures(data);
                if (data.length > 0 && !selectedSignatureId) {
                    setSelectedSignatureId(data[0].id);
                }
            }
        } catch (err) {
            console.error("Failed to fetch signatures:", err);
        }
    };

    useEffect(() => {
        fetchSignatures();
    }, []);

    // Automatically apply default signature for new messages
    useEffect(() => {
        if (signatures.length > 0 && !initialData?.body && newMsgSigId !== 'none') {
            const sig = signatures.find(s => s.id.toString() === newMsgSigId.toString());
            if (sig) {
                // Short delay to ensure editor is initialized
                setTimeout(() => applySignature(sig), 300);
            }
        }
    }, [signatures, initialData]);

    const stylePresets = [
        { name: 'Plain Black', border: '#e5e7eb', header: '#f9fafb', banded: '#f9fafb', accent: '#374151', color: '#facc15' },
        { name: 'Plain Blue', border: '#dbeafe', header: '#eff6ff', banded: '#eff6ff', accent: '#2563eb', color: '#3b82f6' },
        { name: 'Plain Orange', border: '#ffedd5', header: '#fff7ed', banded: '#fff7ed', accent: '#f97316', color: '#f97316' },
        { name: 'Grid Black', border: '#374151', header: '#f3f4f6', banded: '#f9fafb', accent: '#111827', color: '#111827' },
        { name: 'Grid Blue', border: '#0078d4', header: '#e1f0ff', banded: '#f0f7ff', accent: '#005a9e', color: '#0078d4' },
        { name: 'Grid Orange', border: '#f97316', header: '#ffedd5', banded: '#fff7ed', accent: '#c2410c', color: '#c2410c' },
        { name: 'Bold Black', border: '#111827', header: '#111827', banded: '#f3f4f6', accent: '#111827', textOnHeader: '#ffffff', color: '#111827' },
        { name: 'Bold Blue', border: '#005a9e', header: '#0078d4', banded: '#eff6ff', accent: '#005a9e', textOnHeader: '#ffffff', color: '#005a9e' },
        { name: 'Bold Orange', border: '#c2410c', header: '#f97316', banded: '#fff7ed', accent: '#c2410c', textOnHeader: '#ffffff', color: '#f97316' },
    ];
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);

    // Comprehensive emoji data organized by categories
    const emojiCategories = {
        'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😎', '🤓', '🧐'],
        'People': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁', '👅', '👄', '💋'],
        'Nature': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐈', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊', '🐇', '🦝', '🦨', '🦡', '🦦', '🦥', '🐁', '🐀', '🐿', '🦔'],
        'Food': ['🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯'],
        'Activities': ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸', '🥌', '🎿', '⛷', '🏂', '🪂', '🏋️', '🤼', '🤸', '🤺', '⛹️', '🤾', '🏌️', '🏇', '🧘', '🏊', '🤽', '🚣', '🧗', '🚴', '🚵', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🪘', '🎷', '🎺', '🪗', '🎸', '🪕', '🎻', '🎲', '♟', '🎯', '🎳', '🎮', '🎰', '🧩'],
        'Travel': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🦯', '🦽', '🦼', '🛴', '🚲', '🛵', '🏍', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚉', '✈️', '🛫', '🛬', '🛩', '💺', '🛰', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥', '🛳', '⛴', '🚢', '⚓', '⛽', '🚧', '🚦', '🚥', '🚏', '🗺', '🗿', '🗽', '🗼', '🏰', '🏯', '🏟', '🎡', '🎢', '🎠', '⛲', '⛱', '🏖', '🏝', '🏜', '🌋', '⛰', '🏔', '🗻', '🏕', '⛺', '🛖', '🏠', '🏡', '🏘', '🏚', '🏗', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '🏛', '⛪', '🕌', '🕍', '🛕', '🕋'],
        'Objects': ['⌚', '📱', '📲', '💻', '⌨️', '🖥', '🖨', '🖱', '🖲', '🕹', '🗜', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽', '🎞', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚', '🎛', '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯', '🪔', '🧯', '🛢', '💸', '💵', '💴', '💶', '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🪛', '🔧', '🔨', '⚒', '🛠', '⛏', '🪚', '🔩', '⚙️', '🪤', '🧱', '⛓', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡', '⚔️', '🛡', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡', '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', '🪥', '🪒', '🧽', '🪣', '🧴', '🛎', '🔑', '🗝', '🚪', '🪑', '🛋', '🛏', '🛌', '🧸', '🪆', '🖼', '🪞', '🪟', '🛍', '🛒', '🎁', '🎈', '🎏', '🎀', '🪄', '🪅', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌', '📥', '📤', '📦', '🏷', '🪧', '📪', '📫', '📬', '📭', '📮', '📯', '📜', '📃', '📄', '📑', '🧾', '📊', '📈', '📉', '🗒', '🗓', '📆', '📅', '🗑', '📇', '🗃', '🗳', '🗄', '📋', '📁', '📂', '🗂', '🗞', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '🧷', '🔗', '📎', '🖇', '📐', '📏', '🧮', '📌', '📍', '✂️', '🖊', '🖋', '✒️', '🖌', '🖍', '📝', '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓'],
        'Symbols': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🛗', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚧', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '⏏️', '▶️', '⏸', '⏯', '⏹', '⏺', '⏭', '⏮', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂', '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '🟰', '♾', '💲', '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚', '🔙', '🔛', '🔝', '🔜', '✔️', '☑️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '⬛', '⬜', '🟫', '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢', '👁‍🗨', '💬', '💭', '🗯', '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞', '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧']
    };

    React.useEffect(() => {
        if (initialData?.body && editorRef.current) {
            editorRef.current.innerHTML = initialData.body;
        } else if (editorRef.current && editorRef.current.innerHTML === '') {
            editorRef.current.innerHTML = '';
        }

        // Handle To recipients
        if (initialData?.to) {
            const initialRecipients = initialData.to.split(',')
                .map(email => email.trim())
                .filter(Boolean)
                .map(email => ({ email, name: email.split('@')[0] }));
            setToRecipients(initialRecipients);
        }

        // Handle Cc recipients
        if (initialData?.cc) {
            const initialCcRecipients = initialData.cc.split(',')
                .map(email => email.trim())
                .filter(Boolean)
                .map(email => ({ email, name: email.split('@')[0] }));
            setCcRecipients(initialCcRecipients);
            setShowCc(true); // Show Cc field if there are Cc recipients
        }

        // Handle Bcc recipients
        if (initialData?.bcc) {
            const initialBccRecipients = initialData.bcc.split(',')
                .map(email => email.trim())
                .filter(Boolean)
                .map(email => ({ email, name: email.split('@')[0] }));
            setBccRecipients(initialBccRecipients);
            setShowBcc(true); // Show Bcc field if there are Bcc recipients
        }
    }, [initialData]);

    const searchContacts = async (query, type) => {
        if (!query || query.length < 2) {
            if (type === 'to') { setToSearchResults([]); setIsToDropdownOpen(false); }
            if (type === 'cc') { setCcSearchResults([]); setIsCcDropdownOpen(false); }
            if (type === 'bcc') { setBccSearchResults([]); setIsBccDropdownOpen(false); }
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetch(`${API_BASE}/mails/search-contacts?email=${user.email}&query=${query}`, {
                headers: { 'Authorization': `Bearer ${user.token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const currentRecipients = type === 'to' ? toRecipients : type === 'cc' ? ccRecipients : bccRecipients;
                const filtered = data.filter(item => !currentRecipients.some(r => r.email === item.email));

                if (type === 'to') { setToSearchResults(filtered); setIsToDropdownOpen(filtered.length > 0); }
                if (type === 'cc') { setCcSearchResults(filtered); setIsCcDropdownOpen(filtered.length > 0); }
                if (type === 'bcc') { setBccSearchResults(filtered); setIsBccDropdownOpen(filtered.length > 0); }
            }
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const addRecipient = (contact, type) => {
        if (type === 'to') {
            if (!toRecipients.find(r => r.email === contact.email)) setToRecipients([...toRecipients, contact]);
            setToInput('');
            setToSearchResults([]);
            setIsToDropdownOpen(false);
        } else if (type === 'cc') {
            if (!ccRecipients.find(r => r.email === contact.email)) setCcRecipients([...ccRecipients, contact]);
            setCcInput('');
            setCcSearchResults([]);
            setIsCcDropdownOpen(false);
        } else if (type === 'bcc') {
            if (!bccRecipients.find(r => r.email === contact.email)) setBccRecipients([...bccRecipients, contact]);
            setBccInput('');
            setBccSearchResults([]);
            setIsBccDropdownOpen(false);
        }
    };

    const removeRecipient = (email, type) => {
        if (type === 'to') setToRecipients(toRecipients.filter(r => r.email !== email));
        if (type === 'cc') setCcRecipients(ccRecipients.filter(r => r.email !== email));
        if (type === 'bcc') setBccRecipients(bccRecipients.filter(r => r.email !== email));
    };

    const fonts = [
        { name: 'Sans Serif', family: 'sans-serif' },
        { name: 'Serif', family: 'serif' },
        { name: 'Fixed Width', family: 'monospace' },
        { name: 'Wide', family: '"Arial Black", sans-serif' },
        { name: 'Narrow', family: '"Arial Narrow", sans-serif' },
        { name: 'Comic Sans MS', family: '"Comic Sans MS", cursive' },
        { name: 'Garamond', family: 'Garamond, serif' },
        { name: 'Georgia', family: 'Georgia, serif' },
        { name: 'Tahoma', family: 'Tahoma, sans-serif' },
        { name: 'Trebuchet MS', family: '"Trebuchet MS", sans-serif' },
        { name: 'Verdana', family: 'Verdana, sans-serif' },
    ];
    const userInitial = user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U';

    const handleSendMessage = () => {
        const finalTo = toRecipients.map(r => r.email).join(', ') || toInput;
        const finalCc = ccRecipients.map(r => r.email).join(', ') || (showCc ? ccInput : '');
        const finalBcc = bccRecipients.map(r => r.email).join(', ') || (showBcc ? bccInput : '');

        if (!finalTo) {
            alert('Please fill in recipient');
            return;
        }
        const bodyContent = editorRef.current ? editorRef.current.innerHTML : '';
        const styledBody = `<div style="font-family: ${fontFamily}, sans-serif; font-size: 16px; line-height: 1.6;">${bodyContent}</div>`;
        onSend({ ...composeData, to: finalTo, cc: finalCc, bcc: finalBcc, body: styledBody, attachments });
    };

    const handleSaveDraft = () => {
        const finalTo = toRecipients.map(r => r.email).join(', ') || toInput;
        const finalCc = ccRecipients.map(r => r.email).join(', ') || (showCc ? ccInput : '');
        const finalBcc = bccRecipients.map(r => r.email).join(', ') || (showBcc ? bccInput : '');

        const bodyContent = editorRef.current ? editorRef.current.innerHTML : '';
        const styledBody = `<div style="font-family: ${fontFamily}, sans-serif; font-size: 16px; line-height: 1.6;">${bodyContent}</div>`;
        onSaveDraft({ ...composeData, to: finalTo, cc: finalCc, bcc: finalBcc, body: styledBody, attachments });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...files]);
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const getFileIcon = (type) => {
        if (type.startsWith('image/')) return <ImageIcon size={14} />;
        return <FileText size={14} />;
    };

    const execCommand = (command, value = null) => {
        if (editorRef.current) editorRef.current.focus();
        document.execCommand(command, false, value);
        updateActiveFormats();
    };

    const handleFontChange = (fontObj) => {
        setFontFamily(fontObj.name);
        setIsFontDropdownOpen(false);
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.focus();
                // Apply font to selection
                document.execCommand('fontName', false, fontObj.family);
                // Also ensure the base editor style reflects this for new typing
                editorRef.current.style.fontFamily = fontObj.family;
            }
        }, 10);
    };

    const insertEmoji = (emoji) => {
        if (editorRef.current) {
            editorRef.current.focus();
            const selection = window.getSelection();
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const emojiNode = document.createTextNode(emoji);
            range.insertNode(emojiNode);
            range.setStartAfter(emojiNode);
            range.setEndAfter(emojiNode);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        setIsEmojiPickerOpen(false);
    };

    const applySignature = (sig) => {
        if (editorRef.current) {
            const currentHTML = editorRef.current.innerHTML;
            const signatureMarker = '<div class="xemail-signature" style="margin-top: 24px; border-top: 1px solid #eee; padding-top: 12px; font-family: inherit;">';
            const signatureEnd = '</div>';

            let baseContent = currentHTML === 'Start typing your message...' ? '<p><br></p><p><br></p>' : currentHTML;

            if (baseContent.includes('class="xemail-signature"')) {
                const parts = baseContent.split(/<div class="xemail-signature".*?>/);
                baseContent = parts[0] || '<p><br></p><p><br></p>';
            }
            
            // Ensure there's at least some spacing if we're starting a fresh message
            if (baseContent === '') baseContent = '<p><br></p><p><br></p>';

            editorRef.current.innerHTML = baseContent + signatureMarker + sig.content + signatureEnd;
            
            // Move cursor to the beginning so user can start typing immediately
            setTimeout(() => {
                if (editorRef.current) {
                    editorRef.current.focus();
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.setStart(editorRef.current, 0);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            }, 10);
        }
        setIsSignatureDropdownOpen(false);
    };
    useEffect(() => {
        const handleClick = (e) => {
            const table = e.target.closest('table');
            const img = e.target.closest('img');

            if (img && editorRef.current?.contains(img)) {
                setSelectedImage(img);
            } else if (!e.target.closest('.image-resizer')) {
                setSelectedImage(null);
            }

            if (table && editorRef.current?.contains(table)) {
                setSelectedTable(table);
                const rect = table.getBoundingClientRect();
                const editorRect = editorRef.current.getBoundingClientRect();
                setTableToolbarPos({
                    top: rect.top - editorRect.top + editorRef.current.scrollTop,
                    left: rect.left - editorRect.left,
                    width: rect.width,
                    height: rect.height
                });

                // Extract current styles
                setTableStyles({
                    borderColor: table.style.borderColor || '#e5e7eb',
                    borderWidth: parseInt(table.style.borderWidth) || 1,
                    backgroundColor: table.style.backgroundColor || 'transparent',
                    padding: parseInt(table.rows[0]?.cells[0]?.style.padding) || 12
                });
            } else if (!e.target.closest('.table-toolbar')) {
                setSelectedTable(null);
            }
        };

        const editor = editorRef.current;
        if (editor) {
            editor.addEventListener('click', handleClick);
            return () => editor.removeEventListener('click', handleClick);
        }
    }, [isMobile]);

    const standardColors = [
        '#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0'
    ];

    const themeColorGrid = [
        ['#FFFFFF', '#000000', '#E7E6E6', '#44546A', '#5B9BD5', '#ED7D31', '#A5A5A5', '#FFC000', '#4472C4', '#70AD47'],
        ['#F2F2F2', '#7F7F7F', '#D0CECE', '#D6DCE4', '#DEEAF6', '#FBE5D5', '#EDEDED', '#FFF2CC', '#D9E2F3', '#E2EFD9'],
        ['#D8D8D8', '#595959', '#AEAAAA', '#ADB9CA', '#BDD7EE', '#F7CBAC', '#DBDBDB', '#FFE599', '#B4C6E7', '#C5E0B3'],
        ['#BFBFBF', '#3F3F3F', '#757070', '#8496B0', '#9BC2E6', '#F4B083', '#C9C9C9', '#FFD965', '#8EA9DB', '#A8D08D'],
        ['#A5A5A5', '#262626', '#3A3838', '#323E4F', '#2E75B5', '#C55A11', '#7B7B7B', '#BF9000', '#2F5496', '#538135'],
        ['#7F7F7F', '#0C0C0C', '#171616', '#222A35', '#1E4E79', '#843C0B', '#525252', '#7F6000', '#1F3864', '#375623']
    ];

    const highlightColors = [
        '#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#0000FF', '#FF0000', '#000080', '#008080', '#008000', '#800080',
        '#800000', '#808000', '#808080', '#C0C0C0', '#000000'
    ];

    const applyColor = (command, color) => {
        if (editorRef.current) {
            editorRef.current.focus();
            try { document.execCommand('styleWithCSS', false, true); } catch (e) { }

            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                if (range.collapsed) {
                    // Create a style-holding span with a zero-width space for future typing
                    const span = document.createElement('span');
                    if (command === 'foreColor') {
                        span.style.color = color;
                    } else {
                        span.style.backgroundColor = color;
                    }
                    span.innerHTML = '&#8203;'; // ZWSP
                    range.insertNode(span);

                    // Move cursor inside the span after the ZWSP
                    const newRange = document.createRange();
                    newRange.setStart(span.firstChild, 1);
                    newRange.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                } else {
                    document.execCommand(command, false, color);
                    if (command === 'hiliteColor') {
                        document.execCommand('backColor', false, color);
                    }
                }
            }

            if (command === 'foreColor') {
                setTextColor(color);
                setIsTextColorOpen(false);
            } else if (command === 'hiliteColor') {
                setHighlightColor(color);
                setIsHighlightColorOpen(false);
            }
            updateActiveFormats();
        }
    };

    const clearFormatting = () => {
        if (editorRef.current) editorRef.current.focus();
        document.execCommand('removeFormat', false, null);
        updateActiveFormats();
    };

    const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

    const handleFontSizeChange = (size) => {
        setFontSize(size);
        setIsFontSizeDropdownOpen(false);
        if (editorRef.current) {
            editorRef.current.focus();

            // Use execCommand with a temporary size to identify the affected tags
            // This is more stable for preserving cursor position and complex selections
            document.execCommand('fontSize', false, '7');

            const fontElements = Array.from(editorRef.current.getElementsByTagName("font"));
            fontElements.forEach(el => {
                if (el.size === "7") {
                    el.removeAttribute("size");
                    el.style.fontSize = `${size}px`;
                    // If it's empty (just cursor state), it might need a ZWSP inside
                    if (!el.innerHTML || el.innerHTML === '<br>') {
                        el.innerHTML = '&#8203;';
                    }
                }
            });

            updateActiveFormats();
        }
    };

    const adjustFontSize = (delta) => {
        const currentIndex = fontSizes.indexOf(fontSize);
        let newIndex = currentIndex + delta;
        if (newIndex < 0) newIndex = 0;
        if (newIndex >= fontSizes.length) newIndex = fontSizes.length - 1;
        handleFontSizeChange(fontSizes[newIndex]);
    };

    const bulletStyles = [
        { name: 'None', value: 'none', label: 'None' },
        { name: 'Disc', value: 'disc', icon: '●' },
        { name: 'Circle', value: 'circle', icon: '○' },
        { name: 'Square', value: 'square', icon: '■' },
        { name: 'Four Diamonds', value: '"❖ "', icon: '❖' },
        { name: 'Diamonds', value: '"◆ "', icon: '◆' },
        { name: 'Arrow', value: '"➢ "', icon: '➢' },
        { name: 'Checkmark', value: '"✓ "', icon: '✓' }
    ];

    const numberingStyles = [
        { name: 'None', value: 'none', label: 'None' },
        { name: 'Decimal', value: 'decimal', label: '1. 2. 3.' },
        { name: 'Decimal Parenthesis', value: 'decimal', label: '1) 2) 3)', suffix: ')' },
        { name: 'Upper Roman', value: 'upper-roman', label: 'I. II. III.' },
        { name: 'Upper Alpha', value: 'upper-alpha', label: 'A. B. C.' },
        { name: 'Lower Alpha Parenthesis', value: 'lower-alpha', label: 'a) b) c)', suffix: ')' },
        { name: 'Lower Alpha', value: 'lower-alpha', label: 'a. b. c.' },
        { name: 'Lower Roman', value: 'lower-roman', label: 'i. ii. iii.' }
    ];

    const applyListStyle = (type, style, suffix) => {
        if (editorRef.current) editorRef.current.focus();

        const command = type === 'bullet' ? 'insertUnorderedList' : 'insertOrderedList';
        const isAlreadyInList = document.queryCommandState(command);

        if (!isAlreadyInList && style !== 'none') {
            document.execCommand(command, false, null);
        }

        if (style === 'none') {
            if (isAlreadyInList) document.execCommand(command, false, null);
        } else {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                let range = selection.getRangeAt(0);
                let container = range.commonAncestorContainer;
                if (container.nodeType === 3) container = container.parentNode;

                const listTag = type === 'bullet' ? 'UL' : 'OL';
                const listElement = container.closest(listTag);

                if (listElement) {
                    listElement.style.listStyleType = style;
                    listElement.className = ''; // Clear previous suffix classes

                    if (suffix === ')') {
                        if (style === 'decimal') listElement.classList.add('list-suffix-bracket');
                        else if (style === 'lower-alpha') listElement.classList.add('list-suffix-bracket-alpha-lower');
                        else if (style === 'upper-alpha') listElement.classList.add('list-suffix-bracket-alpha-upper');
                        else if (style === 'lower-roman') listElement.classList.add('list-suffix-bracket-roman-lower');
                        else if (style === 'upper-roman') listElement.classList.add('list-suffix-bracket-roman-upper');
                    }
                }
            }
        }
        setIsBulletDropdownOpen(false);
        setIsNumberingDropdownOpen(false);
        updateActiveFormats();
    };

    const updateTableProperty = (prop, value) => {
        if (!selectedTable) return;

        if (prop === 'padding') {
            Array.from(selectedTable.rows).forEach(row => {
                Array.from(row.cells).forEach(cell => {
                    cell.style.padding = `${value}px`;
                });
            });
        } else if (prop === 'borderWidth' || prop === 'borderColor') {
            selectedTable.style[prop] = value + (prop === 'borderWidth' ? 'px' : '');
            Array.from(selectedTable.rows).forEach(row => {
                Array.from(row.cells).forEach(cell => {
                    cell.style[prop] = value + (prop === 'borderWidth' ? 'px' : '');
                });
            });
        } else {
            selectedTable.style[prop] = value;
        }

        setTableStyles(prev => ({ ...prev, [prop]: value }));
    };

    const deleteTable = () => {
        if (selectedTable) {
            selectedTable.remove();
            setSelectedTable(null);
        }
    };


    const insertTable = (rows, cols) => {
        if (editorRef.current && rows > 0 && cols > 0) {
            editorRef.current.focus();

            const preset = stylePresets[selectedTableStyle];
            const tableStyleStr = [
                'border-collapse: collapse',
                `width: ${tableWidth}%`,
                'margin: 10px 0',
                tableAlign === 'Middle' ? 'margin-left: auto; margin-right: auto' : '',
                tableAlign === 'Right' ? 'margin-left: auto' : '',
                `border: 1px solid ${preset.border}`
            ].filter(Boolean).join('; ');

            let tableHTML = `<table style="${tableStyleStr}">`;

            for (let i = 0; i < rows; i++) {
                const isHeader = i === 0 && tableOptions.headerRow;
                const isBanded = tableOptions.bandedRows && i % 2 === 1;

                let rowStyle = '';
                if (isHeader) rowStyle = `background-color: ${preset.header}; color: ${preset.textOnHeader || 'inherit'};`;
                else if (isBanded) rowStyle = `background-color: ${preset.banded};`;

                tableHTML += `<tr style="${rowStyle}">`;
                for (let j = 0; j < cols; j++) {
                    const isFirstCol = j === 0 && tableOptions.firstColumn;
                    const isBandedCol = tableOptions.bandedColumns && j % 2 === 1;

                    let cellStyle = [
                        `border: 1px solid ${preset.border}`,
                        'padding: 12px 15px',
                        'min-width: 60px',
                        isHeader ? 'font-weight: 700' : '',
                        isFirstCol ? 'font-weight: 600' : '',
                        isBandedCol && !isHeader ? `background-color: ${preset.banded}` : ''
                    ].filter(Boolean).join('; ');

                    tableHTML += `<td style="${cellStyle}">&nbsp;</td>`;
                }
                tableHTML += '</tr>';
            }
            tableHTML += '</table><p>&nbsp;</p>';

            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = tableHTML;
                const frag = document.createDocumentFragment();
                let node;
                while ((node = tempDiv.firstChild)) {
                    frag.appendChild(node);
                }
                range.insertNode(frag);

                // Set cursor after table
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
        setIsTablePickerOpen(false);
        setTableHover({ rows: 0, cols: 0 });
        setTableStep('grid');
    };





    return (
        <motion.div
            initial={{ opacity: 0, y: isMobile ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--bg-main)',
                position: isMobile ? 'fixed' : 'relative',
                inset: 0,
                zIndex: 2000,
                height: '100%',
                overflow: 'hidden'
            }}
        >

            {/* Formatting Toolbar */}
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-glass)', background: 'var(--bg-main)', position: 'relative', zIndex: 1100, flexShrink: 0 }}>
                <div className="glass" style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    borderRadius: '12px',
                    gap: isMobile ? '4px' : '8px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                background: activeFormats.bold ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                border: activeFormats.bold ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                borderRadius: '6px'
                            }}
                            onClick={() => execCommand('bold')}
                            title="Bold"
                        >
                            <Bold size={16} />
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                background: activeFormats.italic ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                border: activeFormats.italic ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                borderRadius: '6px'
                            }}
                            onClick={() => execCommand('italic')}
                            title="Italic"
                        >
                            <Italic size={16} />
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                background: activeFormats.underline ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                border: activeFormats.underline ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                borderRadius: '6px'
                            }}
                            onClick={() => execCommand('underline')}
                            title="Underline"
                        >
                            <Underline size={16} />
                        </motion.button>
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div style={{ display: 'flex', gap: '4px' }}>
                        <div className="picker-container" style={{ position: 'relative' }}>
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                onClick={() => togglePicker(setIsEmojiPickerOpen, isEmojiPickerOpen)}
                                className="btn-ghost"
                                style={{ padding: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                title="Insert Emoji"
                            >
                                <Smile size={16} />
                            </motion.button>
                            {isEmojiPickerOpen && (
                                <div className="glass-card scrollbar-hide" style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1200,
                                    padding: '8px',
                                    width: isMobile ? '280px' : '320px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                                }}>
                                    {Object.entries(emojiCategories).map(([category, emojis]) => (
                                        <div key={category} style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: '700',
                                                color: 'var(--text-dim)',
                                                marginBottom: '6px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {category}
                                            </div>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(8, 1fr)',
                                                gap: '4px'
                                            }}>
                                                {emojis.map((emoji, idx) => (
                                                    <motion.button
                                                        key={idx}
                                                        whileHover={{ scale: 1.2 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={() => insertEmoji(emoji)}
                                                        style={{
                                                            fontSize: '1.3rem',
                                                            padding: '4px',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            cursor: 'pointer',
                                                            borderRadius: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'background 0.2s'
                                                        }}
                                                        className="btn-ghost"
                                                    >
                                                        {emoji}
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="picker-container" style={{ position: 'relative' }}>
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                onClick={() => togglePicker(setIsTablePickerOpen, isTablePickerOpen)}
                                className="btn-ghost"
                                style={{ padding: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                title="Insert Table"
                            >
                                <Table size={16} />
                            </motion.button>
                            {isTablePickerOpen && (
                                <div className="glass-card scrollbar-hide" style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1200,
                                    padding: '16px',
                                    width: tableStep === 'grid' ? 'auto' : '300px',
                                    maxHeight: '550px',
                                    overflowY: 'auto',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                                    minWidth: '220px'
                                }}>
                                    {tableStep === 'grid' ? (
                                        <>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: '700',
                                                color: 'var(--text-dim)',
                                                marginBottom: '12px',
                                                textAlign: 'center',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {tableHover.rows > 0 && tableHover.cols > 0
                                                    ? `${tableHover.rows} x ${tableHover.cols} Table`
                                                    : 'Select Dimensions'}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(10, 20px)',
                                                    gap: '3px',
                                                    padding: '4px',
                                                    background: 'var(--bg-secondary)',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--border-glass)'
                                                }}
                                                onMouseLeave={() => setTableHover({ rows: 0, cols: 0 })}
                                            >
                                                {Array.from({ length: 100 }, (_, index) => {
                                                    const row = Math.floor(index / 10) + 1;
                                                    const col = (index % 10) + 1;
                                                    const isHighlighted = row <= tableHover.rows && col <= tableHover.cols;

                                                    return (
                                                        <div
                                                            key={index}
                                                            onMouseEnter={() => setTableHover({ rows: row, cols: col })}
                                                            onClick={() => {
                                                                setTableHover({ rows: row, cols: col });
                                                                setTableStep('customize');
                                                            }}
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                border: '1px solid var(--border-glass)',
                                                                backgroundColor: isHighlighted ? 'var(--primary)' : 'white',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                borderRadius: '3px',
                                                                boxShadow: isHighlighted ? '0 0 8px var(--primary-glow)' : 'none'
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    onClick={() => setTableStep('grid')}
                                                    className="btn-ghost"
                                                    style={{ padding: '4px', borderRadius: '4px' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                                <span style={{ fontSize: '0.9rem', fontWeight: '700', color: 'var(--text-main)' }}>
                                                    Customize {tableHover.rows}x{tableHover.cols} Table
                                                </span>
                                            </div>

                                            {/* Table Styles Preset Grid */}
                                            <div>
                                                <h4 style={{ fontSize: '0.8rem', fontWeight: '700', marginBottom: '8px', color: 'var(--text-dim)', textTransform: 'uppercase' }}>Styles</h4>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                                                    {stylePresets.map((preset, idx) => (
                                                        <motion.div
                                                            key={idx}
                                                            whileHover={{ scale: 1.05 }}
                                                            onClick={() => setSelectedTableStyle(idx)}
                                                            style={{
                                                                height: '45px',
                                                                borderRadius: '8px',
                                                                border: `2.5px solid ${selectedTableStyle === idx ? preset.accent : 'transparent'}`,
                                                                background: 'white',
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                                position: 'relative'
                                                            }}
                                                        >
                                                            <div style={{ height: '35%', background: preset.header }} />
                                                            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' }}>
                                                                <div style={{ borderRight: `0.5px solid ${preset.border}`, borderBottom: `0.5px solid ${preset.border}` }} />
                                                                <div style={{ background: preset.banded }} />
                                                            </div>
                                                            {selectedTableStyle === idx && (
                                                                <div style={{ position: 'absolute', top: 2, right: 2, width: '6px', height: '6px', borderRadius: '50%', background: preset.accent }} />
                                                            )}
                                                        </motion.div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Table Options Checkboxes */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                {[
                                                    { id: 'headerRow', label: 'Header row' },
                                                    { id: 'bandedColumns', label: 'Banded cols' },
                                                    { id: 'firstColumn', label: 'First column' },
                                                    { id: 'bandedRows', label: 'Banded rows' }
                                                ].map(opt => (
                                                    <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <input
                                                            type="checkbox"
                                                            id={opt.id}
                                                            checked={tableOptions[opt.id]}
                                                            onChange={(e) => setTableOptions({ ...tableOptions, [opt.id]: e.target.checked })}
                                                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                                                        />
                                                        <label htmlFor={opt.id} style={{ fontSize: '0.8rem', color: 'var(--text-main)', cursor: 'pointer', fontWeight: '500' }}>{opt.label}</label>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Width & Alignment */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <h4 style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-dim)', textTransform: 'uppercase' }}>Width</h4>
                                                    <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                                                        <input
                                                            type="number"
                                                            value={tableWidth}
                                                            onChange={(e) => setTableWidth(e.target.value)}
                                                            style={{ width: '45px', border: 'none', background: 'transparent', fontSize: '0.9rem', textAlign: 'right', outline: 'none', fontWeight: '700', color: 'var(--text-main)' }}
                                                        />
                                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginLeft: '4px', fontWeight: '600' }}>%</span>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', background: 'var(--bg-secondary)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-glass)' }}>
                                                    {['Left', 'Middle', 'Right'].map(align => (
                                                        <button
                                                            key={align}
                                                            onClick={() => setTableAlign(align)}
                                                            style={{
                                                                flex: 1,
                                                                padding: '8px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '700',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                transition: 'all 0.2s',
                                                                background: tableAlign === align ? 'white' : 'transparent',
                                                                color: tableAlign === align ? 'var(--primary)' : 'var(--text-dim)',
                                                                cursor: 'pointer',
                                                                boxShadow: tableAlign === align ? '0 2px 6px rgba(0,0,0,0.1)' : 'none'
                                                            }}
                                                        >
                                                            {align}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Dimensions Fields */}
                                            <div style={{ display: 'flex', gap: '15px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--text-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Rows</label>
                                                    <input
                                                        type="number"
                                                        value={tableHover.rows}
                                                        onChange={(e) => setTableHover({ ...tableHover, rows: parseInt(e.target.value) || 1 })}
                                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border-glass)', background: 'var(--bg-secondary)', fontSize: '0.9rem', outline: 'none', fontWeight: '700' }}
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--text-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Columns</label>
                                                    <input
                                                        type="number"
                                                        value={tableHover.cols}
                                                        onChange={(e) => setTableHover({ ...tableHover, cols: parseInt(e.target.value) || 1 })}
                                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border-glass)', background: 'var(--bg-secondary)', fontSize: '0.9rem', outline: 'none', fontWeight: '700' }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Insert Button */}
                                            <motion.button
                                                whileHover={{ scale: 1.02, translateY: -2 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => insertTable(tableHover.rows, tableHover.cols)}
                                                style={{
                                                    width: '100%',
                                                    padding: '14px',
                                                    borderRadius: '12px',
                                                    background: 'var(--primary)',
                                                    color: 'white',
                                                    border: 'none',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    fontSize: '0.95rem',
                                                    boxShadow: '0 8px 16px var(--primary-glow)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                <Table size={18} /> Insert Table
                                            </motion.button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="picker-container" style={{ position: 'relative' }}>
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                onClick={() => togglePicker(setIsSignatureDropdownOpen, isSignatureDropdownOpen)}
                                className="btn-ghost"
                                style={{ padding: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                title="Signature"
                            >
                                <PenTool size={16} />
                            </motion.button>
                            {isSignatureDropdownOpen && (
                                <div className="glass-card scrollbar-hide" style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1200,
                                    padding: '4px',
                                    minWidth: '220px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                                }}>
                                    <div
                                        onClick={() => { setIsSignatureDropdownOpen(false); setIsManageSignaturesOpen(true); }}
                                        style={{ padding: '10px 12px', fontSize: '0.9rem', cursor: 'pointer', borderRadius: '6px', borderBottom: '1px solid var(--border-glass)', fontWeight: '600', color: 'var(--primary)' }}
                                        className="btn-ghost"
                                    >
                                        Manage Signatures
                                    </div>
                                    <div
                                        onClick={() => { applySignature({ content: '' }); setIsSignatureDropdownOpen(false); }}
                                        style={{ padding: '10px 12px', fontSize: '0.9rem', cursor: 'pointer', borderRadius: '6px' }}
                                        className="btn-ghost"
                                    >
                                        No Signature
                                    </div>
                                    {signatures.map(sig => (
                                        <div
                                            key={sig.id}
                                            onClick={() => { applySignature(sig); setIsSignatureDropdownOpen(false); }}
                                            style={{ padding: '10px 12px', fontSize: '0.9rem', cursor: 'pointer', borderRadius: '6px' }}
                                            className="btn-ghost"
                                        >
                                            {sig.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>


                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div style={{ display: 'flex', gap: '4px' }}>
                        <div className="picker-container" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                onMouseDown={(e) => e.preventDefault()}
                                className="btn-ghost"
                                style={{
                                    padding: '6px',
                                    background: activeFormats.bullet ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                    border: activeFormats.bullet ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={() => execCommand('insertUnorderedList')}
                                title="Bullet List"
                            >
                                <List size={16} />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                onMouseDown={(e) => e.preventDefault()}
                                className="btn-ghost"
                                style={{
                                    padding: '6px 2px',
                                    background: isBulletDropdownOpen ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                    border: isBulletDropdownOpen ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                    borderLeft: 'none',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={() => togglePicker(setIsBulletDropdownOpen, isBulletDropdownOpen)}
                                title="Bullet Library"
                            >
                                <ChevronDown size={10} style={{ opacity: 0.5 }} />
                            </motion.button>

                            {isBulletDropdownOpen && (
                                <div className="glass-card" style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1200,
                                    padding: '16px',
                                    width: '260px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                                }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: 'var(--text-main)', marginBottom: '12px' }}>Bullet Library</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                                        {bulletStyles.map((s, idx) => (
                                            <motion.div
                                                key={idx}
                                                whileHover={{ scale: 1.05, background: '#f8f9fa' }}
                                                onClick={() => applyListStyle('bullet', s.value)}
                                                style={{
                                                    height: '50px',
                                                    border: '1px solid var(--border-glass)',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    fontSize: s.label ? '0.75rem' : '1.3rem',
                                                    background: 'white',
                                                    color: 'black',
                                                    fontWeight: '700'
                                                }}
                                            >
                                                {s.label || s.icon}
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="picker-container" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                onMouseDown={(e) => e.preventDefault()}
                                className="btn-ghost"
                                style={{
                                    padding: '6px',
                                    background: activeFormats.numbering ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                    border: activeFormats.numbering ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={() => execCommand('insertOrderedList')}
                                title="Numbered List"
                            >
                                <ListOrdered size={16} />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                onMouseDown={(e) => e.preventDefault()}
                                className="btn-ghost"
                                style={{
                                    padding: '6px 2px',
                                    background: isNumberingDropdownOpen ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                    border: isNumberingDropdownOpen ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                    borderLeft: 'none',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={() => togglePicker(setIsNumberingDropdownOpen, isNumberingDropdownOpen)}
                                title="Numbering Library"
                            >
                                <ChevronDown size={10} style={{ opacity: 0.5 }} />
                            </motion.button>

                            {isNumberingDropdownOpen && (
                                <div className="glass-card" style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1200,
                                    padding: '16px',
                                    width: '280px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                                }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: 'var(--text-main)', marginBottom: '12px' }}>Numbering Library</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                                        {numberingStyles.map((s, idx) => (
                                            <motion.div
                                                key={idx}
                                                whileHover={{ scale: 1.05, background: '#f8f9fa' }}
                                                onClick={() => applyListStyle('numbering', s.value, s.suffix)}
                                                style={{
                                                    height: '55px',
                                                    border: '1px solid var(--border-glass)',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    fontSize: '0.7rem',
                                                    background: 'white',
                                                    color: 'black',
                                                    padding: '4px',
                                                    textAlign: 'center',
                                                    lineHeight: '1.2',
                                                    fontWeight: '700'
                                                }}
                                            >
                                                {s.label}
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => togglePicker(setIsAlignDropdownOpen, isAlignDropdownOpen)}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                background: isAlignDropdownOpen ? 'var(--bg-secondary, #f0f0f0)' : 'transparent',
                                border: isAlignDropdownOpen ? '1px solid var(--border-glass, #ddd)' : '1px solid transparent',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px'
                            }}
                            title="Alignment"
                        >
                            {activeFormats.alignCenter ? <AlignCenter size={16} /> :
                                activeFormats.alignRight ? <AlignRight size={16} /> :
                                    activeFormats.alignJustify ? <AlignJustify size={16} /> :
                                        <AlignLeft size={16} />}
                            <ChevronDown size={10} style={{ opacity: 0.5 }} />
                        </motion.button>

                        {isAlignDropdownOpen && (
                            <div className="glass-card" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '4px',
                                display: 'flex',
                                gap: '2px',
                                background: 'white',
                                border: '1px solid var(--border-glass, #ddd)',
                                borderRadius: '8px',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                            }}>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="btn-ghost"
                                    style={{
                                        padding: '8px',
                                        background: activeFormats.alignLeft ? 'var(--bg-secondary)' : 'transparent',
                                        borderRadius: '6px'
                                    }}
                                    onClick={() => { execCommand('justifyLeft'); setIsAlignDropdownOpen(false); }}
                                    title="Align Left"
                                >
                                    <AlignLeft size={16} />
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="btn-ghost"
                                    style={{
                                        padding: '8px',
                                        background: activeFormats.alignCenter ? 'var(--bg-secondary)' : 'transparent',
                                        borderRadius: '6px'
                                    }}
                                    onClick={() => { execCommand('justifyCenter'); setIsAlignDropdownOpen(false); }}
                                    title="Align Center"
                                >
                                    <AlignCenter size={16} />
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="btn-ghost"
                                    style={{
                                        padding: '8px',
                                        background: activeFormats.alignRight ? 'var(--bg-secondary)' : 'transparent',
                                        borderRadius: '6px'
                                    }}
                                    onClick={() => { execCommand('justifyRight'); setIsAlignDropdownOpen(false); }}
                                    title="Align Right"
                                >
                                    <AlignRight size={16} />
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className="btn-ghost"
                                    style={{
                                        padding: '8px',
                                        background: activeFormats.alignJustify ? 'var(--bg-secondary)' : 'transparent',
                                        borderRadius: '6px'
                                    }}
                                    onClick={() => { execCommand('justifyFull'); setIsAlignDropdownOpen(false); }}
                                    title="Justify"
                                >
                                    <AlignJustify size={16} />
                                </motion.button>
                            </div>
                        )}
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onClick={() => togglePicker(setIsAttachmentDropdownOpen, isAttachmentDropdownOpen)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-main)', cursor: 'pointer', background: 'var(--bg-secondary)', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}
                            title="Attach"
                        >
                            <Paperclip size={16} />
                            <ChevronDown size={12} />
                        </motion.button>
                        {isAttachmentDropdownOpen && (
                            <div className="glass-card scrollbar-hide" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '4px',
                                minWidth: '160px',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                            }}>
                                <div
                                    onMouseDown={(e) => { e.preventDefault(); fileInputRef.current.click(); setIsAttachmentDropdownOpen(false); }}
                                    style={{ padding: '8px 12px', fontSize: '0.96rem', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    className="btn-ghost"
                                >
                                    <FileText size={14} /> Attach Files
                                </div>
                                <div
                                    onMouseDown={(e) => { e.preventDefault(); folderInputRef.current.click(); setIsAttachmentDropdownOpen(false); }}
                                    style={{ padding: '8px 12px', fontSize: '0.96rem', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    className="btn-ghost"
                                >
                                    <Folder size={14} /> Attach Folder
                                </div>
                                <div
                                    onMouseDown={(e) => { e.preventDefault(); fileInputRef.current.accept = "image/*"; fileInputRef.current.click(); setIsAttachmentDropdownOpen(false); }}
                                    style={{ padding: '8px 12px', fontSize: '0.96rem', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    className="btn-ghost"
                                >
                                    <ImageIcon size={14} /> Attach Images
                                </div>
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileChange} />
                        <input type="file" ref={folderInputRef} style={{ display: 'none' }} webkitdirectory="true" onChange={handleFileChange} />
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative' }}>
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => togglePicker(setIsFontSizeDropdownOpen, isFontSizeDropdownOpen)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-main)', cursor: 'pointer', background: 'var(--bg-secondary)', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-glass)', minWidth: '45px', justifyContent: 'center' }}
                        >
                            <span>{fontSize}</span>
                            <ChevronDown size={12} />
                        </motion.div>
                        {isFontSizeDropdownOpen && (
                            <div className="glass-card scrollbar-hide" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '4px',
                                minWidth: '80px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                            }}>
                                {fontSizes.map(size => (
                                    <div
                                        key={size}
                                        onMouseDown={(e) => { e.preventDefault(); handleFontSizeChange(size); }}
                                        style={{ padding: '6px 12px', fontSize: '0.9rem', cursor: 'pointer', borderRadius: '6px', backgroundColor: fontSize === size ? 'var(--primary-glow)' : 'transparent', textAlign: 'center' }}
                                        className="btn-ghost"
                                    >
                                        {size}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>



                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative' }}>
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => togglePicker(setIsFontDropdownOpen, isFontDropdownOpen)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-main)', cursor: 'pointer', background: 'var(--bg-secondary)', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-glass)' }}
                        >
                            <span style={{ maxWidth: isMobile ? '40px' : '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fontFamily}</span>
                            <ChevronDown size={12} />
                        </motion.div>
                        {isFontDropdownOpen && (
                            <div className="glass-card scrollbar-hide" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '4px',
                                minWidth: '150px',
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}>
                                {fonts.map(font => (
                                    <div
                                        key={font.name}
                                        onMouseDown={(e) => { e.preventDefault(); handleFontChange(font); }}
                                        style={{ 
                                            padding: '10px 12px', 
                                            fontSize: '1rem', 
                                            cursor: 'pointer', 
                                            fontFamily: font.family, 
                                            borderRadius: '6px', 
                                            backgroundColor: fontFamily === font.name ? 'var(--bg-secondary)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            transition: 'all 0.2s',
                                            color: 'var(--text-main)'
                                        }}
                                        className="font-item-hover"
                                    >
                                        <span style={{ 
                                            fontWeight: font.name === 'Wide' ? '900' : 'normal',
                                            fontStyle: font.name === 'Comic Sans MS' ? 'italic' : 'normal'
                                        }}>
                                            {font.name}
                                        </span>
                                        {fontFamily === font.name && <Check size={14} style={{ color: 'var(--primary)' }} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                border: '1px solid transparent',
                                borderRadius: '6px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '1px',
                                position: 'relative'
                            }}
                            onClick={() => applyColor('foreColor', textColor)}
                            title="Text Color"
                        >
                            <span style={{ fontSize: '1.05rem', fontWeight: '800', lineHeight: 1 }}>A</span>
                            <div style={{ width: '14px', height: '3px', background: textColor, borderRadius: '1px' }} />
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px 2px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={() => togglePicker(setIsTextColorOpen, isTextColorOpen)}
                        >
                            <ChevronDown size={10} style={{ opacity: 0.5 }} />
                        </motion.button>

                        {isTextColorOpen && (
                            <div className="glass-card" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '12px',
                                width: '260px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                            }}>
                                <div
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applyColor('foreColor', '#000000')}
                                    style={{ padding: '6px 8px', fontSize: '0.85rem', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
                                    className="btn-ghost"
                                >
                                    <div style={{ width: '14px', height: '14px', background: '#000000', border: '1px solid #ddd' }} />
                                    Automatic
                                </div>

                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-main)', marginBottom: '8px', paddingLeft: '4px' }}>Theme Colors</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', marginBottom: '12px' }}>
                                    {themeColorGrid.map((row, rowIdx) => (
                                        <div key={rowIdx} style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '1px' }}>
                                            {row.map((color, colIdx) => (
                                                <motion.div
                                                    key={colIdx}
                                                    whileHover={{ scale: 1.15, zIndex: 10 }}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => applyColor('foreColor', color)}
                                                    style={{
                                                        width: '22px',
                                                        height: '22px',
                                                        background: color,
                                                        cursor: 'pointer',
                                                        border: '1px solid rgba(0,0,0,0.05)',
                                                        marginTop: rowIdx === 1 ? '4px' : '0' // Add gap between base row and tints
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-main)', marginBottom: '8px', paddingLeft: '4px' }}>Standard Colors</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '1px', marginBottom: '12px' }}>
                                    {standardColors.map(color => (
                                        <motion.div
                                            key={color}
                                            whileHover={{ scale: 1.15, zIndex: 10 }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => applyColor('foreColor', color)}
                                            style={{ width: '22px', height: '22px', background: color, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.05)' }}
                                        />
                                    ))}
                                </div>

                                <div
                                    style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '8px', marginTop: '4px' }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        const input = document.createElement('input');
                                        input.type = 'color';
                                        input.onchange = (e) => applyColor('foreColor', e.target.value);
                                        input.click();
                                    }}
                                    className="btn-ghost"
                                >
                                    <div style={{ padding: '4px 8px', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Palette size={14} />
                                        More Colors...
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--border-glass)' }} />

                    <div className="picker-container" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px',
                                border: '1px solid transparent',
                                borderRadius: '6px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '1px'
                            }}
                            onClick={() => applyColor('hiliteColor', highlightColor)}
                            title="Highlight Color"
                        >
                            <Highlighter size={16} />
                            <div style={{ width: '14px', height: '3px', background: highlightColor, borderRadius: '1px' }} />
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={(e) => e.preventDefault()}
                            className="btn-ghost"
                            style={{
                                padding: '6px 2px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={() => togglePicker(setIsHighlightColorOpen, isHighlightColorOpen)}
                        >
                            <ChevronDown size={10} style={{ opacity: 0.5 }} />
                        </motion.button>

                        {isHighlightColorOpen && (
                            <div className="glass-card" style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)',
                                left: 0,
                                zIndex: 1200,
                                padding: '12px',
                                width: '180px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                            }}>
                                <div
                                    onClick={() => applyColor('hiliteColor', 'transparent')}
                                    style={{ padding: '6px 8px', fontSize: '0.85rem', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
                                    className="btn-ghost"
                                >
                                    <div style={{ width: '14px', height: '14px', background: 'transparent', border: '1px solid #ddd', position: 'relative', overflow: 'hidden' }}>
                                        <div style={{ position: 'absolute', top: '50%', left: '-20%', width: '140%', height: '1px', background: 'red', transform: 'rotate(45deg)' }} />
                                    </div>
                                    No Color
                                </div>

                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-dim)', marginBottom: '8px', paddingLeft: '4px' }}>Highlight Library</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                                    {highlightColors.map(color => (
                                        <motion.div
                                            key={color}
                                            whileHover={{ scale: 1.15 }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => applyColor('hiliteColor', color)}
                                            style={{ width: '22px', height: '22px', background: color, borderRadius: '4px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.1)' }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Address Fields - Scrollable only if needed but kept compact */}
            <div style={{ padding: isMobile ? '0 12px' : '0 20px', borderBottom: '1px solid var(--border-glass)', background: 'var(--bg-main)', flexShrink: 0 }}>
                <div style={{ marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-glass)', gap: '12px' }}>
                        <span style={{ minWidth: isMobile ? '40px' : '60px', color: 'var(--text-dim)', fontWeight: '600', fontSize: '0.85rem' }}>From</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                            {(() => {
                                // Strictly show ONLY ONE mail based on context
                                const contextEmail = activeFolder.startsWith('Shared/') 
                                    ? activeFolder.substring(7) 
                                    : (user?.email || 'koushik@xevyte.co');
                                
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                        <div style={{ 
                                            width: '24px', 
                                            height: '24px', 
                                            borderRadius: '50%', 
                                            background: 'var(--primary)', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            color: 'white', 
                                            fontSize: '0.75rem', 
                                            fontWeight: '700', 
                                            flexShrink: 0,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            {contextEmail.charAt(0).toUpperCase()}
                                        </div>
                                        <span style={{ 
                                            color: 'var(--text-main)', 
                                            fontWeight: '600', 
                                            fontSize: '0.9rem', 
                                            fontFamily: 'Inter, sans-serif',
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {contextEmail}
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    <RecipientInput
                        label="To"
                        recipients={toRecipients}
                        searchInput={toInput}
                        setSearchInput={setToInput}
                        searchResults={toSearchResults}
                        isDropdownOpen={isToDropdownOpen}
                        setIsDropdownOpen={setIsToDropdownOpen}
                        type="to"
                        addRecipient={addRecipient}
                        removeRecipient={removeRecipient}
                        searchContacts={searchContacts}
                        showCc={showCc}
                        setShowCc={setShowCc}
                        showBcc={showBcc}
                        setShowBcc={setShowBcc}
                    />

                    {showCc && (
                        <RecipientInput
                            label="Cc"
                            recipients={ccRecipients}
                            searchInput={ccInput}
                            setSearchInput={setCcInput}
                            searchResults={ccSearchResults}
                            isDropdownOpen={isCcDropdownOpen}
                            setIsDropdownOpen={setIsCcDropdownOpen}
                            type="cc"
                            addRecipient={addRecipient}
                            removeRecipient={removeRecipient}
                            searchContacts={searchContacts}
                        />
                    )}

                    {showBcc && (
                        <RecipientInput
                            label="Bcc"
                            recipients={bccRecipients}
                            searchInput={bccInput}
                            setSearchInput={setBccInput}
                            searchResults={bccSearchResults}
                            isDropdownOpen={isBccDropdownOpen}
                            setIsDropdownOpen={setIsBccDropdownOpen}
                            type="bcc"
                            addRecipient={addRecipient}
                            removeRecipient={removeRecipient}
                            searchContacts={searchContacts}
                        />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: 'none', gap: '12px' }}>
                        <span style={{ minWidth: isMobile ? '40px' : '60px', color: 'var(--text-dim)', fontWeight: '600', fontSize: '0.85rem' }}>Subject</span>
                        <input
                            placeholder="Subject"
                            value={composeData.subject}
                            onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.95rem', fontWeight: '400', background: 'transparent', color: 'var(--text-main)' }}
                        />
                    </div>
                </div>
            </div>

            {/* Scrollable Editor Area */}
            <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div style={{ padding: isMobile ? '12px' : '16px 20px', paddingBottom: '120px' }}>
                    {/* Attachments List */}
                    {attachments.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', padding: '8px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                            {attachments.map((file, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '4px 10px',
                                        background: 'white',
                                        border: '1px solid var(--border-glass)',
                                        borderRadius: '8px',
                                        fontSize: '0.8rem',
                                        color: 'var(--text-main)'
                                    }}
                                >
                                    {getFileIcon(file.type)}
                                    <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                    <button
                                        onClick={() => removeAttachment(index)}
                                        style={{ color: 'var(--text-dim)', border: 'none', background: 'none' }}
                                    >
                                        <X size={14} />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    <div style={{ position: 'relative' }}>
                        <div
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning={true}
                            style={{
                                width: '100%',
                                minHeight: '300px',
                                border: 'none',
                                outline: 'none',
                                fontSize: '1rem',
                                color: 'var(--text-main)',
                                lineHeight: '1.6',
                                fontFamily: fontFamily,
                                padding: '8px 4px',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word'
                            }}
                            onFocus={(e) => { if (e.target.innerHTML === 'Start typing your message...') e.target.innerHTML = ''; }}
                            onKeyUp={updateActiveFormats}
                            onMouseUp={updateActiveFormats}
                            onInput={updateActiveFormats}
                        >
                            Start typing your message...
                        </div>
                        {selectedImage && (
                            <ImageResizer 
                                img={selectedImage}
                                containerRef={editorRef}
                                onClose={() => setSelectedImage(null)}
                            />
                        )}
                    </div>

                    {/* Table Editing Toolbar removed as per user request */}
                </div>
            </div>

            {/* Action Bar at the Bottom */}
            <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border-glass)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'var(--bg-main)',
                flexShrink: 0
            }}>
                <button
                    onClick={handleSendMessage}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 24px',
                        background: 'var(--primary)',
                        color: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px var(--primary-glow)'
                    }}
                >
                    <SendHorizontal size={18} />
                    Send
                </button>

                <button
                    onClick={handleSaveDraft}
                    style={{
                        padding: '10px 24px',
                        background: 'transparent',
                        color: 'var(--text-main)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-glass)',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        cursor: 'pointer'
                    }}
                >
                    Draft
                </button>

                <div style={{ flex: 1 }} />

                <button
                    onClick={() => { if (window.confirm('Discard changes?')) onClose(); }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        background: 'transparent',
                        color: 'var(--text-muted)',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s'
                    }}
                    title="Discard"
                >
                    <Trash2 size={20} />
                    Discard
                </button>
            </div>

            <SignaturesModal
                isOpen={isManageSignaturesOpen}
                onClose={() => setIsManageSignaturesOpen(false)}
                signatures={signatures}
                setSignatures={setSignatures}
                selectedSignatureId={selectedSignatureId}
                setSelectedSignatureId={setSelectedSignatureId}
                isEditingSignature={isEditingSignature}
                setIsEditingSignature={setIsEditingSignature}
                editName={editName}
                setEditName={setEditName}
                editContent={editContent}
                setEditContent={setEditContent}
                sigEditorRef={sigEditorRef}
                fetchSignatures={fetchSignatures}
                signedAccount={signedAccount}
                setSignedAccount={setSignedAccount}
                newMsgSigId={newMsgSigId}
                setNewMsgSigId={setNewMsgSigId}
                replySigId={replySigId}
                setReplySigId={setReplySigId}
            />
        </motion.div >
    );
};

const SignaturesModal = ({
    isOpen,
    onClose,
    signatures,
    setSignatures,
    selectedSignatureId,
    setSelectedSignatureId,
    isEditingSignature,
    setIsEditingSignature,
    editName,
    setEditName,
    editContent,
    setEditContent,
    sigEditorRef,
    fetchSignatures,
    signedAccount,
    setSignedAccount,
    newMsgSigId,
    setNewMsgSigId,
    replySigId,
    setReplySigId
}) => {
    if (!isOpen) return null;
    const sigs = signatures || [];
    const selectedSig = sigs.find(s => s.id === selectedSignatureId);

    const handleSaveSignature = async () => {
        if (!editName.trim()) return;
        const contentToSave = sigEditorRef.current ? sigEditorRef.current.innerHTML : editContent;

        const isNew = selectedSignatureId && sigs.find(s => s.id === selectedSignatureId)?.isNew;
        const method = isNew ? 'POST' : 'PUT';
        const endpoint = isNew ? '/signatures' : `/signatures/${selectedSignatureId}`;

        if (!localStorage.getItem('token') && !localStorage.getItem('xemail_user')) {
            alert("Your session has expired. Please log in again.");
            return;
        }

        const payload = {
            name: editName,
            content: contentToSave
        };

        try {
            console.log(`[DEBUG] Saving signature. Payload size: ${JSON.stringify(payload).length} characters.`);
            const resp = await apiFetch(endpoint, {
                method,
                body: JSON.stringify(payload)
            });
            if (resp.ok) {
                setIsEditingSignature(false);
                fetchSignatures();
            } else {
                const errData = await resp.text();
                console.error(`[ERROR] Signature save failed with status ${resp.status}:`, errData);
                alert(`Failed to save [${resp.status}]: ${errData || 'Forbidden or Request Too Large'}`);
            }
        } catch (err) {
            console.error("Failed to save signature:", err);
            alert(`Error saving signature: ${err.message}`);
        }
    };

    const handleAddNew = () => {
        const newId = Date.now();
        setSignatures([...signatures, { id: newId, name: 'New Signature', content: '', isNew: true }]);
        setSelectedSignatureId(newId);
        setEditName('New Signature');
        setEditContent('');
        setIsEditingSignature(true);
    };

    const handleDelete = async () => {
        if (!selectedSignatureId) return;

        const sigToDelete = sigs.find(s => s.id === selectedSignatureId);
        if (sigToDelete?.isNew) {
            setSignatures(sigs.filter(s => s.id !== selectedSignatureId));
            setSelectedSignatureId(null);
            setIsEditingSignature(false);
            return;
        }

        try {
            const resp = await apiFetch(`/signatures/${selectedSignatureId}`, { method: 'DELETE' });
            if (resp.ok) {
                setSelectedSignatureId(null);
                fetchSignatures();
            }
        } catch (err) {
            console.error("Failed to delete signature:", err);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 4000
        }}>
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                style={{
                    width: '750px',
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                    overflow: 'hidden',
                    color: '#1a1a1a',
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                }}
            >
                {/* Neutral Title Bar */}
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', background: 'white', borderBottom: '1px solid #eee' }}>
                    <div style={{ flex: 1, fontSize: '1.1rem', fontWeight: '700' }}>Email Signatures</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                            <input placeholder="Search signatures..." style={{ padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.85rem', width: '200px', outline: 'none' }} />
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '8px',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                color: '#666',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s'
                            }}
                            className="btn-ghost"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div style={{ padding: '24px' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '16px' }}>Edit signature:</div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 1.5fr', gap: '20px', marginBottom: '24px' }}>
                        {/* Left Pane: List */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', background: 'white' }}>
                                <div style={{ background: '#fcfcfc', padding: '10px 16px', fontSize: '0.85rem', fontWeight: '600', borderBottom: '1px solid #eee', color: '#666' }}>All Signatures</div>
                                <div style={{ height: '180px', overflowY: 'auto' }}>
                                    {sigs.map(sig => (
                                        <div
                                            key={sig.id}
                                            onClick={() => {
                                                setSelectedSignatureId(sig.id);
                                                setIsEditingSignature(false);
                                            }}
                                            style={{
                                                padding: '10px 16px',
                                                fontSize: '0.9rem',
                                                cursor: 'pointer',
                                                backgroundColor: selectedSignatureId === sig.id ? 'var(--primary-light, #e1f0ff)' : 'transparent',
                                                color: selectedSignatureId === sig.id ? '#0078d4' : 'inherit',
                                                fontWeight: selectedSignatureId === sig.id ? '600' : '400'
                                            }}
                                        >
                                            {sig.name}
                                        </div>
                                    ))}
                                    <div style={{ height: '1px', background: '#eee' }} />
                                </div>
                                <div style={{ display: 'flex', borderTop: '1px solid #eee', background: '#f8f8f8' }}>
                                    <button onClick={handleAddNew} style={{ padding: '6px 12px', borderRight: '1px solid #eee', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}>+</button>
                                    <button onClick={handleDelete} style={{ padding: '6px 12px', borderRight: '1px solid #eee', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}>-</button>
                                    <div style={{ flex: 1 }} />
                                    <button
                                        onClick={() => {
                                            if (selectedSig) {
                                                setEditName(selectedSig.name);
                                                setEditContent(selectedSig.content);
                                                setIsEditingSignature(true);
                                            }
                                        }}
                                        style={{ padding: '4px 12px', borderLeft: '1px solid #eee', border: 'none', background: 'transparent', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>

                            {/* Signature Selection Defaults */}
                            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #eee' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: '12px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <PenTool size={14} /> Signature defaults
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px' }}>For new messages:</label>
                                        <select
                                            value={newMsgSigId}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setNewMsgSigId(val);
                                                localStorage.setItem('defaultNewMessageSigId', val);
                                            }}
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.85rem', outline: 'none', background: 'white' }}
                                        >
                                            <option value="none">(none)</option>
                                            {sigs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px' }}>For replies/forwards:</label>
                                        <select
                                            value={replySigId}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setReplySigId(val);
                                                localStorage.setItem('defaultReplySigId', val);
                                            }}
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.85rem', outline: 'none', background: 'white' }}
                                        >
                                            <option value="none">(none)</option>
                                            {sigs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Pane: Preview / Edit */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ background: '#f8f8f8', padding: '4px 12px', fontSize: '0.8rem', border: '1px solid #ccc', borderBottom: 'none', textAlign: 'center', borderRadius: '6px 6px 0 0', color: '#666' }}>
                                {isEditingSignature ? 'Edit Signature' : 'Signature Preview'}
                            </div>
                            <div style={{
                                border: '1px solid #ccc',
                                borderRadius: '0 0 6px 6px',
                                flex: 1,
                                padding: '20px',
                                background: 'white',
                                minHeight: '220px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {isEditingSignature ? (
                                    <>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                            <input
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                placeholder="Signature Name"
                                                style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none' }}
                                            />
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <input
                                                    type="file"
                                                    id="sig-image-upload"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file && sigEditorRef.current) {
                                                            const reader = new FileReader();
                                                            reader.onload = (event) => {
                                                                const imgElement = new Image();
                                                                imgElement.onload = () => {
                                                                    const canvas = document.createElement('canvas');
                                                                    const MAX_WIDTH = 300;
                                                                    let width = imgElement.width;
                                                                    let height = imgElement.height;

                                                                    if (width > MAX_WIDTH) {
                                                                        height *= MAX_WIDTH / width;
                                                                        width = MAX_WIDTH;
                                                                    }

                                                                    canvas.width = width;
                                                                    canvas.height = height;
                                                                    const ctx = canvas.getContext('2d');
                                                                    // Use better image smoothing
                                                                    ctx.imageSmoothingEnabled = true;
                                                                    ctx.imageSmoothingQuality = 'high';
                                                                    ctx.drawImage(imgElement, 0, 0, width, height);

                                                                    // Use lossless PNG for maximum quality/clarity
                                                                    const resizedDataUrl = canvas.toDataURL('image/png');
                                                                    const imgTag = `<img src="${resizedDataUrl}" style="width: ${width}px; height: auto; margin: 8px 0; display: block;" />`;

                                                                    sigEditorRef.current.focus();
                                                                    document.execCommand('insertHTML', false, imgTag);
                                                                };
                                                                imgElement.src = event.target.result;
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                        e.target.value = ''; // Reset for same file re-upload
                                                    }}
                                                />
                                                <button
                                                    onClick={() => document.getElementById('sig-image-upload').click()}
                                                    style={{
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #ddd',
                                                        background: 'white',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        color: '#666'
                                                    }}
                                                    className="btn-ghost"
                                                    title="Add Logo/Image"
                                                >
                                                    <ImageIcon size={16} />
                                                    <span style={{ fontSize: '0.85rem' }}>Logo</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            ref={sigEditorRef}
                                            contentEditable
                                            suppressContentEditableWarning={true}
                                            onBlur={(e) => setEditContent(e.target.innerHTML)}
                                            dangerouslySetInnerHTML={{ __html: editContent }}
                                            style={{
                                                flex: 1,
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '10px',
                                                overflowY: 'auto',
                                                outline: 'none',
                                                minHeight: '150px',
                                                background: 'white'
                                            }}
                                        />
                                        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            <button onClick={() => setIsEditingSignature(false)} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ccc' }}>Cancel</button>
                                            <button onClick={handleSaveSignature} style={{ padding: '4px 10px', borderRadius: '4px', background: '#0078d4', color: 'white', border: 'none' }}>Save</button>
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        dangerouslySetInnerHTML={{ __html: selectedSig?.content || '<span style="color: #999">Select a signature to preview</span>' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </motion.div>
        </div>
    );
};

export default ComposeModal;
